// src\features\Cart\store\transactionStore.ts
import type { CartItem } from "@/types/cart.type";
import { normalizeDateForInput } from "@/utils/normalizeDateForInput";
import { create } from "zustand";

// ===== Types =====
type TransactionData = {
  cart: CartItem[];
  tanggalTransaksi: string;
  metodePembayaran: string;
  customer: string | null;
  customerName?: string | null;
  diskonSubtotal: number; // selalu rupiah
  ppn: number; // selalu rupiah
  paidAmount: number;
  changeAmount: number;
  out_no?: string | null;
};

type TransactionActions = {
  addToCart: (
    product: Omit<CartItem, "qty" | "discount" | "subtotal">,
    qty?: number
  ) => void;
  removeFromCart: (mtrl_code: string) => void;
  updateQty: (mtrl_code: string, qty: number) => void;
  updateDiscount: (
    mtrl_code: string,
    discount: number // selalu rupiah
  ) => void;
  clearCart: () => void;
  setTanggalTransaksi: (tanggal: string) => void;
  setMetodePembayaran: (metode: string) => void;
  setCustomer: (custid: string | null, customerName?: string | null) => void;
  setDiskonSubtotal: (diskon: number) => void;
  setPPN: (ppnValue: number) => void;
  setPaidAmount: (paid: number | ((current: number) => number)) => void;
  clearTransaction: () => void;
  replaceTransaction: (data: TransactionData) => void;
};

type TransactionState = TransactionData & TransactionActions;

// ===== Constants =====
const STORAGE_KEY = "transaction";

// ===== Helper Functions =====
const calculateSubtotal = (item: CartItem): number => {
  const price = Number(item.sell_price);
  const qty = Number(item.qty);
  const subtotal = price * qty;
  const discountRupiah = Number(item.discount) || 0;

  // discount selalu dalam rupiah (total diskon untuk baris ini)
  const result = subtotal - discountRupiah;
  return result > 0 ? result : 0;
};

const getInitialData = (): TransactionData => ({
  cart: [],
  tanggalTransaksi: new Date().toLocaleDateString("id-ID"),
  metodePembayaran: "Cash",
  customer: null,
  customerName: "Umum",
  diskonSubtotal: 0,
  ppn: 0,
  paidAmount: 0,
  changeAmount: 0,
  out_no: null,
});

const saveToLocalStorage = (data: TransactionData): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save to localStorage:", error);
  }
};

const getFromLocalStorage = (): TransactionData => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<TransactionData>;
      return {
        ...getInitialData(),
        ...parsed,
      };
    }
  } catch (error) {
    console.error("Failed to load from localStorage:", error);
  }
  return getInitialData();
};

const clearLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear localStorage:", error);
  }
};

// ===== Zustand Store =====
export const useTransactionStore = create<TransactionState>((set, _) => {
  const initialData = getFromLocalStorage();

  return {
    // Initial Data
    ...initialData,

    // Actions
    addToCart: (product, qty = 1) => {
      set((state) => {
        const existing = state.cart.find((i) => i.mtrl_code === product.mtrl_code);
        let updatedCart: CartItem[];

        if (existing) {
          updatedCart = state.cart.map((item) =>
            item.mtrl_code === product.mtrl_code
              ? {
                ...item,
                qty: item.qty + qty,
                subtotal: calculateSubtotal({ ...item, qty: item.qty + qty }),
              }
              : item
          );
        } else {
          const newItem: CartItem = {
            ...product,
            qty,
            discount: 0, // rupiah
            subtotal: calculateSubtotal({
              ...product,
              qty,
              discount: 0,
            } as CartItem),
          };
          updatedCart = [...state.cart, newItem];
        }

        const newData: TransactionData = {
          ...state,
          cart: updatedCart,
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    removeFromCart: (mtrl_code) => {
      set((state) => {
        const updatedCart = state.cart.filter((item) => item.mtrl_code !== mtrl_code);

        const newData: TransactionData = {
          ...state,
          cart: updatedCart,
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    updateQty: (mtrl_code, qty) => {
      set((state) => {
        const updatedCart = state.cart.map((item) =>
          item.mtrl_code === mtrl_code
            ? { ...item, qty, subtotal: calculateSubtotal({ ...item, qty }) }
            : item
        );

        const newData: TransactionData = {
          ...state,
          cart: updatedCart,
          diskonSubtotal: 0,
          ppn: 0,
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    updateDiscount: (mtrl_code, discount) => {
      set((state) => {
        const updatedCart = state.cart.map((cartItem) =>
          cartItem.mtrl_code === mtrl_code
            ? {
              ...cartItem,
              discount, // sudah rupiah
              subtotal: calculateSubtotal({
                ...cartItem,
                discount,
              }),
            }
            : cartItem
        );

        const newData: TransactionData = {
          ...state,
          cart: updatedCart,
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    clearCart: () => {
      set((state) => {
        const newData: TransactionData = {
          ...state,
          cart: [],
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    setTanggalTransaksi: (tanggal) => {
      // pastikan selalu ISO
      const isoDate =
        /^\d{4}-\d{2}-\d{2}$/.test(tanggal)
          ? tanggal
          : normalizeDateForInput(tanggal);

      set((state) => {
        const newData = {
          ...state,
          tanggalTransaksi: isoDate,
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    setMetodePembayaran: (metode) => {
      set((state) => {
        const newData: TransactionData = {
          ...state,
          metodePembayaran: metode,
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    /**
     * Set customer detail for transaction
     * @param {string} custid
     * @param {string|null} customerName
     * @returns {TransactionData} updated transaction data
     */
    setCustomer: (custid, customerName = null) => {
      set((state) => {
        const newData: TransactionData = {
          ...state,
          customer: custid,
          customerName: customerName || null,
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    setDiskonSubtotal: (diskon) => {
      set((state) => {
        const newData: TransactionData = {
          ...state,
          diskonSubtotal: diskon, // sudah rupiah
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    setPPN: (ppnValue) => {
      set((state) => {
        const newData: TransactionData = {
          ...state,
          ppn: ppnValue, // sudah rupiah
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    setPaidAmount: (paid) => {
      set((state) => {
        const newPaidAmount = typeof paid === "function" ? paid(state.paidAmount) : Number(paid) || 0;
        const totalSubtotal = state.cart.reduce((sum, item) => sum + item.subtotal, 0);
        const afterDiscount = totalSubtotal - state.diskonSubtotal;
        const totalFinal = afterDiscount + state.ppn;

        const newData: TransactionData = {
          ...state,
          paidAmount: newPaidAmount,
          changeAmount: newPaidAmount - totalFinal,
        };

        saveToLocalStorage(newData);
        return newData;
      });
    },

    clearTransaction: () => {
      clearLocalStorage();
      const newData = getInitialData();
      saveToLocalStorage(newData);
      set(newData);
    },

    replaceTransaction: (data: TransactionData) => {
      clearLocalStorage();
      set(data);
    },
  };
});

// ===== Selectors =====
export const selectCart = (state: TransactionState) => state.cart;

export const selectCartTotal = (state: TransactionState) =>
  state.cart.reduce((sum, item) => sum + item.subtotal, 0);

export const selectGrandTotal = (state: TransactionState) => {
  const subtotal = state.cart.reduce((sum, item) => sum + item.subtotal, 0);
  const afterDiscount = subtotal - state.diskonSubtotal;
  return afterDiscount + state.ppn;
};

export const selectCartItemCount = (state: TransactionState) =>
  state.cart.reduce((sum, item) => sum + item.qty, 0);

export const selectDiskonSubtotalAmount = (state: TransactionState) => {
  return state.diskonSubtotal;
};

export const selectPPNAmount = (state: TransactionState) => {
  return state.ppn;
};

export const selectAfterDiscount = (state: TransactionState) => {
  const subtotal = state.cart.reduce((sum, item) => sum + item.subtotal, 0);
  return subtotal - state.diskonSubtotal;
};